import React, { useState, useMemo } from 'react'
import 'react-dates/initialize'
import { DateRangePicker, CalendarDay } from 'react-dates'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { css } from '@emotion/core'
import { Button } from '../atoms'
import { ButtonType } from '../atoms/button'

type DateRangeFieldProps = {
  beginDate: string | undefined
  endDate: string | undefined
  openDirection?: 'up' | 'down'
  changeRangeDate: (beginDate, endDate) => void
  customCss?: ReturnType<typeof css>
  withPortal?: boolean
  // 売り上げ管理用: カレンダーを開いたときに選択中の範囲の月を表示する
  useSalesManagerBehavior?: boolean
  /** 開始日を開いたときも選択中の期間の月を出す。未指定時は今日の月（共通の既定） */
  showSelectedPeriod?: boolean
  /** showSelectedPeriod で期間が未選択のときに出す月（YYYY-MM-DD）。未指定時は今日の月 */
  emptyPeriodVisibleMonth?: string
  /**
   * 選択不可日の判定。開始日／終了日のどちらを選んでいるかで条件を変えられるよう
   * 現在フォーカス中の入力も渡す
   */
  isOutsideRange?: (day: moment.Moment, focusedInput: 'startDate' | 'endDate' | null) => boolean
  /**
   * プリセット（今日・先週など）のボタンを隠す。
   * プリセットは isOutsideRange を通らずに範囲を確定させるため、選択不可日を厳密に守りたい画面で使う
   */
  hidePresets?: boolean
}

export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  beginDate,
  endDate,
  changeRangeDate,
  openDirection = 'down',
  customCss,
  withPortal,
  useSalesManagerBehavior = false,
  showSelectedPeriod = false,
  emptyPeriodVisibleMonth,
  isOutsideRange,
  hidePresets = false,
}) => {
  const { t } = useTranslation()
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate' | null>(null)

  // 売り上げ管理用の動作: カレンダーを開いたときに表示する月: 選択中ならその範囲の月、なければ今月
  // 通常の動作: endDateを選択する時（focusedInput === 'endDate'）に、beginDateと同じ月を表示する
  const initialVisibleMonth = useMemo(() => {
    return () => {
      if (showSelectedPeriod) {
        if (beginDate) return moment(beginDate)
        if (endDate) return moment(endDate)
        return emptyPeriodVisibleMonth ? moment(emptyPeriodVisibleMonth) : moment()
      }
      if (useSalesManagerBehavior) {
        // 売り上げ管理用: 選択中ならその範囲の月、なければ今月
        if (focusedInput === 'endDate' && beginDate) {
          return moment(beginDate)
        }
        if (beginDate) {
          return moment(beginDate)
        }
        if (endDate) {
          return moment(endDate)
        }
        return moment()
      } else {
        // 通常の動作: endDateを選択する時（focusedInput === 'endDate'）に、beginDateと同じ月を表示する
        if (focusedInput === 'endDate' && beginDate) {
          return moment(beginDate)
        }
        return moment()
      }
    }
  }, [focusedInput, beginDate, endDate, useSalesManagerBehavior, showSelectedPeriod, emptyPeriodVisibleMonth])
  const [selectedRange, setSelectedRange] = useState<
    'yesterday' | 'lastWeek' | 'lastMonth' | 'today' | 'tomorrow' | 'thisWeek' | 'thisMonth' | 'nextMonth'
  >()

  const onClose = ({ endDate }) => {
    // After selecting only the starting point, reset if you leave
    if (endDate === null) {
      changeRangeDate('', '')
    }
  }

  const onChange = event => {
    // In the start point selection, always reset the end point when changing the date
    if (focusedInput === 'startDate') {
      changeRangeDate(event.startDate?.format('YYYY-MM-DD'), '')
      return
    }
    changeRangeDate(event.startDate ? event.startDate?.format('YYYY-MM-DD') : '', event.endDate ? event.endDate?.format('YYYY-MM-DD') : '')
  }

  const infoButtonCommonProps = (isSelected: boolean) => ({
    buttonType: isSelected ? ButtonType.Simple : ButtonType.NonStyle,
    width: 48,
    height: 24,
    fontSize: 12,
  })

  const onChangePreset = range => {
    if (range === selectedRange) {
      return
    }
    setSelectedRange(range)

    let from = moment()
    let to = moment()

    if (range === 'yesterday') {
      from = moment().subtract(1, 'day')
      to = moment().subtract(1, 'day')
    }
    if (range === 'lastWeek') {
      from = moment().subtract(7, 'day')
      to = moment().subtract(1, 'day')
    }
    if (range === 'lastMonth') {
      from = moment().subtract(1, 'month').startOf('month')
      to = moment().subtract(1, 'month').endOf('month')
    }
    if (range === 'tomorrow') {
      from = moment().add(1, 'day')
      to = moment().add(1, 'day')
    }
    if (range === 'thisWeek') {
      to = moment().add(6, 'day')
    }
    if (range === 'thisMonth') {
      from = moment().startOf('month')
      to = moment().endOf('month')
    }
    if (range === 'nextMonth') {
      from = moment().add(1, 'month').startOf('month')
      to = moment().add(1, 'month').endOf('month')
    }
    changeRangeDate(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
    setFocusedInput(null)
  }

  const dateRangeFieldStyle = css({
    zIndex: 101,
    display: 'inline-block',
    position: 'relative',
    '.CalendarDay__outside': { color: '#cccccc' },
    '.DateRangePicker_picker': {
      zIndex: 101,
    },
    '.DateInput input': {
      fontWeight: 'normal',
    },
  })

  return (
    <div css={[dateRangeFieldStyle, hidePresets && presetsHiddenStyle, customCss]}>
      <DateRangePicker
        key={focusedInput === 'endDate' && beginDate ? `endDate-${beginDate}` : 'default'}
        displayFormat={t('MM-DD-YYYY')}
        startDate={beginDate ? moment(beginDate) : null}
        startDateId="startDate"
        startDatePlaceholderText={t('Date')}
        endDate={endDate ? moment(endDate) : null}
        endDateId="endDate"
        endDatePlaceholderText={t('Date')}
        onDatesChange={arg => {
          setSelectedRange(undefined)
          onChange(arg)
        }}
        focusedInput={focusedInput}
        onFocusChange={setFocusedInput}
        onClose={onClose}
        openDirection={openDirection}
        readOnly={true}
        withPortal={withPortal}
        customArrowIcon={<span style={{ color: beginDate !== null ? '#272727' : '#cccccc' }}>~</span>}
        numberOfMonths={2}
        minimumNights={0}
        monthFormat={t('MMM-YY')}
        hideKeyboardShortcutsPanel={true}
        firstDayOfWeek={0}
        daySize={42}
        isOutsideRange={day => isOutsideRange?.(day, focusedInput) ?? false}
        initialVisibleMonth={initialVisibleMonth}
        customInputIcon={<img alt="calendar" src={require('@/static/images/calendar_icon.svg')} width={14} />}
        renderCalendarInfo={
          hidePresets
            ? undefined
            : () => (
                <div css={infoButtonStyle}>
                  <div>
                    <div className="grid-label">{t('Future')}:</div>
                    <div className="button-grid">
                      <Button
                        onClick={() => {
                          onChangePreset('today')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'today')}
                      >
                        {t('Today')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('tomorrow')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'tomorrow')}
                      >
                        {t('Tomorrow')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('thisWeek')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'thisWeek')}
                      >
                        {t('This week')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('thisMonth')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'thisMonth')}
                      >
                        {t('This month')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('nextMonth')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'nextMonth')}
                      >
                        {t('Next month')}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="grid-label">{t('Past')}:</div>
                    <div className="button-grid">
                      <Button
                        onClick={() => {
                          onChangePreset('yesterday')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'yesterday')}
                      >
                        {t('Yesterday')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('lastWeek')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'lastWeek')}
                      >
                        {t('Last week')}
                      </Button>

                      <Button
                        onClick={() => {
                          onChangePreset('lastMonth')
                        }}
                        {...infoButtonCommonProps(selectedRange === 'lastMonth')}
                      >
                        {t('Last month')}
                      </Button>
                    </div>
                  </div>
                </div>
              )
        }
        navPrev={
          <div className="monthPrev">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_left.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
            />
          </div>
        }
        navNext={
          <div className="monthNext">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_right.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
            />
          </div>
        }
        renderCalendarDay={props => (
          <CalendarDay
            {...props}
            renderDayContents={day => (
              <div className="day">
                <span>{day?.format('D')}</span>
              </div>
            )}
          />
        )}
      />
    </div>
  )
}

// datepicker-custom.css はプリセット欄込みの高さでカレンダーを固定しているため、
// プリセットを出さないときは高さを中身に合わせて余白が残らないようにする
const presetsHiddenStyle = css({
  '.DateRangePickerInput .DayPicker__horizontal': {
    height: 'auto',
  },
})

const infoButtonStyle = css({
  borderTop: '1px solid rgb(217, 217, 217)',
  padding: '8px 22px',
  '> div': {
    paddingTop: 8,
    display: 'flex',
    ':first-of-type': { paddingTop: 0 },
    '.grid-label': {
      lineHeight: '24px',
      paddingRight: 8,
    },
    '.button-grid': {
      flexGrow: 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, 48px)',
      gap: 8,
    },
  },
})
